import { Helpers } from '~commonInfra/utils/Helpers';

import { getSessionManagementServiceInstance, SessionManagementService } from '../sessionManagement/SessionManagementService';
import AxiosHttpClient from './AxiosHttpClient';
import { IFormDataPostRequestParams } from './serverApi/types/IFormDataPostRequestParams';
import { IRequest } from './serverApi/types/IRequest';
import { IResult } from './serverApi/types/IResponse';
import { SendRequest } from './serverApi/types/IServer';

/**
 * This library implements HTTP client for Web Client APIs.
 * Web Client APIs serve most of the Tableau Server UI - VizPortalClient
 */
namespace WebHttpClient {

  /**
   * Prefix for the Web Client API, which is how most VizPortal Client requests are made.
   * @constant
   * @type {string}
   * @default
   */
  export const API_PREFIX: string = 'vizportal/api/web/v1/';

  function getSessionManagementService(): SessionManagementService {
    return getSessionManagementServiceInstance();
  }

  /**
   * Makes http GET request to Web Client API. The request does not wait for getSessionInfo call.
   * @param {IRequest} request - request object
   * @returns {Promise} Promise that resolves with server response
   */
  export function requestGet<TParams, TResult extends IResult>(request: IRequest<TParams, TResult>): Promise<TResult> {
    let queryParams = '';
    if (request.params) {

      const requestParamsAsQueryParams = Helpers.serialize(request.params as Object);
      if (requestParamsAsQueryParams) {
        queryParams = '?' + requestParamsAsQueryParams;
      }
    }
    return AxiosHttpClient.makeAxiosRequest<TResult>({
      method: 'GET',
      url: API_PREFIX + request.method + queryParams
    });
  }

  /**
   * Makes http POST request to Web Client API. The request does not wait for getSessionInfo call. Use {sendRequest} if the request must wait for getSessionInfo call.
   * @param {IRequest} request - request object
   * @returns {Promise} Promise that resolves with server response
   */
  export function requestPost<TParams, TResult extends IResult>(request: IRequest<TParams, TResult>): Promise<TResult> {
    return AxiosHttpClient.makeAxiosRequest<TResult>({
      method: 'POST',
      url: API_PREFIX + request.method,
      data: {
        'method': request.method,
        'params': request.params
      },
      headers: request.headers
    });
  }

  /**
   * Make a server request using the Web Client API. The request is made `within` session, aka after getSessionInfo call finishes
   * @param {IRequest} request - request object
   * @returns {Promise} Promise that resolves with server response
   */
  export const sendRequest: SendRequest = function sendRequest<TParams, TResult>(request: IRequest<TParams, TResult>): Promise<TResult> {
    return getSessionManagementService().sendRequestWithinSession(() => requestPost(request));
  };

  /**
   * Send a series of requests of the same type.
   * The results are combined using the given function.
   * If any request results in a broken promise,
   * then this method returns that broken promise.
   */
  export function sendRequests<TItem, TParams, TResult>(
    requests: IRequest<TParams, TResult>[],
    combineResults: (results: TResult[]) => TResult
  ): Promise<TResult> {
    const sendNextRequest = (index: number, results: TResult[]): Promise<TResult> => {
      return sendRequest(requests[index]).then((result: TResult) => {
        results[index] = result;
        const nextIndex = index + 1;
        if (nextIndex < requests.length) {
          return sendNextRequest(nextIndex, results);
        } else {
          return combineResults(results);
        }
      });
    };
    return sendNextRequest(0, []);
  }

  /**
   * Like sendRequests, but sends the requests in parallel.
   */
  export function sendRequestsParallel<TItem, TParams, TResult>(
    requests: IRequest<TParams, TResult>[],
    combineResults: (results: TResult[]) => TResult
  ): Promise<TResult> {
    const results: Promise<TResult>[] = requests.map(
      (request: IRequest<TParams, TResult>) => {
        return sendRequest(request);
      }
    );

    return Promise.all(results).then((results: TResult[]) => {
      return combineResults(results);
    });
  }

  /**
   * File Upload client code for Web Client REST api
   * Note: this is NOT the VizPortal REST API - see RestApiService.ts
   */

  /**
   * Prefix for the Web Client REST API, which is used for file uploads.
   * @constant
   * @type {string}
   * @default
   */
  export const FILE_UPLOAD_API_PREFIX: string = 'vizportal/api/rest/v1/';

  export function requestPostFormDataToRestApi(params: IFormDataPostRequestParams): Promise<any> {
    const formData: FormData = new FormData();
    const formDataFields = params.formDataFields;
    for (const i in formDataFields) {
      const formDataField = formDataFields[i];
      const formDataFieldKey = Object.keys(formDataField)[0];
      formData.append(formDataFieldKey, formDataField[formDataFieldKey]);
    }
    return requestPostFormToRestApi(formData, params.method);
  }

  export function requestPostFormToRestApi(formData: FormData, method: string): Promise<IResult> {
    return AxiosHttpClient.makeAxiosRequest({
      method: 'POST',
      // Do not serialize into JSON (which is Angular's default) so we can send binary data
      transformRequest: (data: any) => data,
    // Allow browser to choose Content-Type to get boundry needed for binary data
      headers: { 'Content-Type': undefined },
      url: FILE_UPLOAD_API_PREFIX + method,
      data: formData
    });
  }
}

export default WebHttpClient;
